import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAllIdClients,
  fetchAllMarkets,
  fetchRefresh,
  fetchSKUCompanies,
  fetchCompanyAllBrands,
  createNewRegion,
} from "../api/internal_tool_endpoints";

export const fetchAllIdClientsAction = createAsyncThunk(
  "internalTool/fetchAllIdClients",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAllIdClients();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllMarketsAction = createAsyncThunk(
  "internalTool/fetchAllMarkets",
  async ({ id_client }, { rejectWithValue }) => {
    try {
      return await fetchAllMarkets({ id_client });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRefreshAction = createAsyncThunk(
  "internalTool/fetchRefresh",
  async ({ id_client }, { rejectWithValue }) => {
    try {
      return await fetchRefresh({ id_client });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSKUCompaniesAction = createAsyncThunk(
  "internalTool/fetchSKUCompanies",
  async ({ id_client, country }, { rejectWithValue }) => {
    try {
      const result = await fetchSKUCompanies({ id_client, country });
      return { country, companies: result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCompanyAllBrandsAction = createAsyncThunk(
  "internalTool/fetchCompanyAllBrands",
  async ({ id_client, country, company }, { rejectWithValue }) => {
    try {
      const result = await fetchCompanyAllBrands({ id_client, country, company });
      return { company, brands: result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createNewRegionAction = createAsyncThunk(
  "internalTool/createNewRegion",
  async ({ id_client, regions_info }, { rejectWithValue }) => {
    try {
      return await createNewRegion({ id_client, regions_info });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  clientIds: null,
  clientIdsLoading: false,
  clientIdsError: null,
  markets: null,
  marketsLoading: false,
  marketsError: null,
  refreshData: null,
  refreshLoading: false,
  refreshError: null,
  skuCompanies: {},
  skuCompaniesLoading: false,
  skuCompaniesError: null,
  companyBrands: {},
  companyBrandsLoading: false,
  companyBrandsError: null,
};

const internalToolSlice = createSlice({
  name: "internalTool",
  initialState,
  reducers: {
    clearMarkets: (state) => {
      state.markets = null;
      state.marketsLoading = false;
      state.marketsError = null;
    },
    clearRefreshData: (state) => {
      state.refreshData = null;
      state.refreshLoading = false;
      state.refreshError = null;
    },
    clearSKUCompanies: (state) => {
      state.skuCompanies = {};
      state.skuCompaniesLoading = false;
      state.skuCompaniesError = null;
    },
    clearCompanyBrands: (state) => {
      state.companyBrands = {};
      state.companyBrandsLoading = false;
      state.companyBrandsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllIdClients
      .addCase(fetchAllIdClientsAction.pending, (state) => {
        state.clientIdsLoading = true;
        state.clientIdsError = null;
      })
      .addCase(fetchAllIdClientsAction.fulfilled, (state, action) => {
        state.clientIdsLoading = false;
        state.clientIds = action.payload;
      })
      .addCase(fetchAllIdClientsAction.rejected, (state, action) => {
        state.clientIdsLoading = false;
        state.clientIdsError = action.payload;
      })
      // fetchAllMarkets
      .addCase(fetchAllMarketsAction.pending, (state) => {
        state.marketsLoading = true;
        state.marketsError = null;
      })
      .addCase(fetchAllMarketsAction.fulfilled, (state, action) => {
        state.marketsLoading = false;
        state.markets = action.payload;
      })
      .addCase(fetchAllMarketsAction.rejected, (state, action) => {
        state.marketsLoading = false;
        state.marketsError = action.payload;
      })
      // fetchRefresh
      .addCase(fetchRefreshAction.pending, (state) => {
        state.refreshLoading = true;
        state.refreshError = null;
      })
      .addCase(fetchRefreshAction.fulfilled, (state, action) => {
        state.refreshLoading = false;
        state.refreshData = action.payload;
      })
      .addCase(fetchRefreshAction.rejected, (state, action) => {
        state.refreshLoading = false;
        state.refreshError = action.payload;
      })
      // fetchSKUCompanies — keyed by country
      .addCase(fetchSKUCompaniesAction.pending, (state) => {
        state.skuCompaniesLoading = true;
        state.skuCompaniesError = null;
      })
      .addCase(fetchSKUCompaniesAction.fulfilled, (state, action) => {
        state.skuCompaniesLoading = false;
        const { country, companies } = action.payload;
        state.skuCompanies[country] = companies;
      })
      .addCase(fetchSKUCompaniesAction.rejected, (state, action) => {
        state.skuCompaniesLoading = false;
        state.skuCompaniesError = action.payload;
      })
      // fetchCompanyAllBrands — keyed by company
      .addCase(fetchCompanyAllBrandsAction.pending, (state) => {
        state.companyBrandsLoading = true;
        state.companyBrandsError = null;
      })
      .addCase(fetchCompanyAllBrandsAction.fulfilled, (state, action) => {
        state.companyBrandsLoading = false;
        const { company, brands } = action.payload;
        state.companyBrands[company] = brands;
      })
      .addCase(fetchCompanyAllBrandsAction.rejected, (state, action) => {
        state.companyBrandsLoading = false;
        state.companyBrandsError = action.payload;
      })
      // createNewRegion — no state updates needed
      .addCase(createNewRegionAction.pending, () => {})
      .addCase(createNewRegionAction.fulfilled, () => {})
      .addCase(createNewRegionAction.rejected, () => {});
  },
});

export const { clearMarkets, clearRefreshData, clearSKUCompanies, clearCompanyBrands } =
  internalToolSlice.actions;

export default internalToolSlice.reducer;
